import {autorun, IDisposer, React} from '@api'
import {CatalogItemCardShape, CatalogItemShapeUtil, ShapeComponentStore, TDBaseShape, TDShapeType, TldrawApp, WhiteboardStore} from '@modules/whiteboard'
import {CardImageSourceSettings, StoresInstance} from '@stores'
import {action, computed, observable, runInAction} from 'mobx'
import {TLComponentProps} from '@tldraw/core'
import {AssortmentRootStore, BuilderCardStore, VipLocalStore} from '@modules'

export interface MyProps extends TLComponentProps<CatalogItemCardShape> {
  app: TldrawApp
}

export class CatalogItemShapeStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: MyProps) {
    super(stores)

    this.baseStore = new ShapeComponentStore(props, this)
  }

  baseStore : ShapeComponentStore<CatalogItemCardShape, CatalogItemShapeUtil>

  get shape() { return this.props.shape}
  get whiteboard() { return this.props.app}

  @observable showDetailsPopoverWhenSessionIsFinished = false

  @computed get imageSize(): { width; height } {
    const {shape: {size: [width, height]}, whiteboard: {camera: {zoom}}} = this

    return {width: width * 4, height: height * 4}
  }

  registerEffects = () => {
    let initialRender = true
    const {whiteboard} = this
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {cardData, cardData: {showDetailsPopover}} = this
        if (showDetailsPopover) {
          const {shape: {point}} = this,
            copy = _.cloneDeep(point)

          runInAction(() => {
            if (initialRender) initialRender = false
            else {
              if (whiteboard.isInSession) {
                cardData.showDetailsPopover = false
                this.showDetailsPopoverWhenSessionIsFinished = true
              }
            }
          })
        }
      }, {name: `Turn off details card when we're moved`}),
      autorun(() => {
        if (this.showDetailsPopoverWhenSessionIsFinished && !whiteboard.isInSession) {
          runInAction(() => {
            this.cardData.showDetailsPopover = true
            this.showDetailsPopoverWhenSessionIsFinished = false
          })
        }
      })
    ]

    return () => {
      delete this.whiteboard.shapeComponentStores[this.shape.id]
      _toDispose.forEach(d => d())
    }
  }

  @computed get alignSelf() {
    const {shape} = this
    switch (shape.verticalAlign) {
      case 'top':
        return 'flex-start'

      case 'bottom':
        return 'flex-end'

      case 'centerVertical':
      default:
        return 'center'
    }
  }
  @computed get justifySelf() {
    const {shape} = this
    switch (shape.horizontalAlign) {
      case 'left':
        return 'flex-start'

      case 'right':
        return 'flex-end'

      case 'centerHorizontal':
      default:
        return 'center'
    }
  }

  @observable.ref ref: HTMLDivElement
  @action setRef = r => this.ref = r

  @observable.ref card: BuilderCardStore
  @action onSetCardStore = c => {
    this.card = c
  }

  @computed get builderCardPopoverProps() {
    const {onSetPopoverCardStore, shape: {cardData: {itemId, imageId}}, visibleCardFields, workspaceConfig} = this

    return {
      onSetCardStore: onSetPopoverCardStore,
      visibleCardFields,
      id: itemId,
      image: imageId ? {exId: imageId} : undefined,
      workspace: workspaceConfig,
      isPopover: true,
      levelOfDetail: 1
    }
  }

  @computed get builderCardProps() {
    const {onSetCardStore, shape: {cardData: {itemId, imageId, view}}, visibleCardFields, workspaceConfig} = this

    return {
      onSetCardStore: onSetCardStore,
      visibleCardFields,
      id: itemId,
      image: imageId ? {exId: imageId} : undefined,
      workspace: workspaceConfig,
      isPopover: true,
      showListPreviewCard: view === 'listPreviewCard'
    }
  }
  @observable.ref popoverCard: BuilderCardStore
  @action onSetPopoverCardStore = c => {
    this.popoverCard = c
  }

  @computed get workspaceConfig() {
    const {props: {shape: {cardData}}, workspaces: {byId}} = this
    return cardData?.workspace ? byId[cardData?.workspace] : null
  }

  @computed get workspace() {
    const {props: {shape: {cardData}}, workspaces: {wrapperById}} = this
    return cardData?.workspace ? wrapperById[cardData?.workspace] : null
  }

  @computed get style() {
    const {shape: {size: [width, height]}, alignSelf, justifySelf, cardData: {view}} = this

    return {
      width,
      height,
      '--shape-image-align-self': alignSelf,
      '--shape-image-justify-self': justifySelf,
      '--shape-width': `${width}px`,
      '--shape-height': `${height}px`
    } as any
  }

  @computed get visibleCardFields() {
    const {shape: {cardData: {visibleFields}}, workspace} = this
    return workspace ? (visibleFields ?? []).map(f => workspace.catalog._fieldByName[f]).filter(_.identity) : []

  }
  get isSelected() { return this.props.isSelected}

  @computed get title() {
    const {card} = this
    if (!card) return null

    const {data, dataMap} = card
    return _.get(data, dataMap.title)
  }

  get cardData() {
    return this.shape.cardData
  }

  @action toggleShowPopover = () => {
    this.cardData.showDetailsPopover = this.cardData.showDetailsPopover != true
  }
}

export const CatalogItemShapeStoreContext = React.createContext<CatalogItemShapeStore>(null)
export const useCatalogItemShapeStore = () => React.useContext(CatalogItemShapeStoreContext)




// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/catalogItem/CatalogItemShapeStore.tsx